import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Observable, from, of } from "rxjs";
import { UserService } from "../services/user.service";
import { CognitoService } from "../services/cognito.service";
import { CognitoUser } from "amazon-cognito-identity-js";
import { BrowserService } from "../services/browser.service";
import {
  makeStateKey,
  StateKey,
  TransferState,
} from "@angular/platform-browser";
import { isPlatformServer, isPlatformBrowser } from "@angular/common";
import { tap } from "rxjs/operators";

@Injectable()
export class HttpAuthorizationInterceptor implements HttpInterceptor {
  constructor(
    private userService: UserService,
    private browserService: BrowserService,
    private cognitoService: CognitoService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
  }
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // set header to let HTTP_INTERCEPTORS know to handle or not.
    // { headers: { 'Anonymous': '' } }
    // to skip the anonymous http request
    const key: StateKey<string> = makeStateKey<string>(request.url);
    if (isPlatformServer(this.platformId)) {
      if (request.headers.get("Anonymous")) {
        const newHeaders = request.headers.delete("Anonymous");
        const newRequest = request.clone({ headers: newHeaders });
        // return next.handle(newRequest);
        return next.handle(newRequest).pipe(
          tap((event) => {
            if ((<HttpResponse<any>>event).body) {
              this.transferState.set(key, (<HttpResponse<any>>event).body);
            }
          })
        );
      } else {
        // return from(this.handleAccess(request, next));
        return from(this.handleAccess(request, next)).pipe(
          tap((event) => {
            if ((<HttpResponse<any>>event).body)
              this.transferState.set(key, (<HttpResponse<any>>event).body);
          })
        );
      }
    } else if (isPlatformBrowser(this.platformId)) {
      const storedResponse = this.transferState.get<any>(key, null);
      if (storedResponse) {
        const response = new HttpResponse({
          body: storedResponse,
          status: 200,
        });
        this.transferState.remove(key);
        return of(response);
      } else {
        if (request.headers.get("Anonymous")) {
          const newHeaders = request.headers.delete("Anonymous");
          const newRequest = request.clone({ headers: newHeaders });
          return next.handle(newRequest);
        } else {
          return from(this.handleAccess(request, next));
        }
      }
    }

    // if (isPlatformServer(this.platformId)) {
    //   return next.handle(request).pipe(tap((event) => {
    //     if (event instanceof HttpResponse) {
    //         this.transferState.set(key, (<HttpResponse<any>> event).body);
    //     }
    //   }));
    // } else {
    //   const storedResponse = this.transferState.get<any>(key, null);
    //   if (storedResponse) {
    //     const response = new HttpResponse({body: storedResponse, status: 200});
    //     this.transferState.remove(key);
    //     return of(response);
    //   } else {
    //     return next.handle(request);
    //   }
    // }
  }
  /**
   * This function to solve the probelm of using async into interceptor
   */
  private async handleAccess(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Promise<HttpEvent<any>> {
    this.cognitoService.initCognitoUserPool();
    const cognitoUser = this.cognitoService.userPool.getCurrentUser();
    if (cognitoUser) {
      const idToken = await this.getIdToken(cognitoUser);
      request = request.clone({
        setHeaders: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      return next.handle(request).toPromise();
    } else if (
      this.userService.getUserData() &&
      typeof Storage != "undefined"
    ) {
      this.cognitoService
        .authWithTokens({
          email: this.userService.getUserData().email,
          accessToken: this.browserService
            .getLocalstorage()
            .getItem("accessToken"),
          idToken: this.browserService.getLocalstorage().getItem("idToken"),
          refreshToken: this.browserService
            .getLocalstorage()
            .getItem("refreshToken"),
        })
        .then(async (resp) => {
          const newCognitoUser = this.cognitoService.userPool.getCurrentUser();
          const idToken = await this.getIdToken(newCognitoUser);
          request = request.clone({
            setHeaders: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
          });
          return next.handle(request).toPromise();
        });
    } else {
      request = request.clone({
        setHeaders: {
          "Content-Type": "application/json",
        },
      });
      return next.handle(request).toPromise();
    }
  }
  /**
   * This function check if the idToken is valid & return the correct idToken
   */
  private async getIdToken(cognitoUser: CognitoUser): Promise<string> {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession(async (err, session) => {
        if (session) {
          const sessionExpirationTimeSecs = session.accessToken.payload.exp;
          const timeNowSecs = new Date().getTime() / 1000 + 300;
          if (!session.isValid() || sessionExpirationTimeSecs <= timeNowSecs) {
            const newSession = await this.cognitoService.renew(
              session.refreshToken.token
            );
            resolve(newSession.id_token);
          } else {
            resolve(session.idToken.jwtToken);
          }
        } else {
          resolve("");
        }
      });
    });
  }
}
